import { CSSProperties, ComponentType, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DraggableAttributes,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Stack } from '@components/stack';
import { Spacing } from '@styles/theme';

export type SortableListProps<T, Item> = {
  onChange: (activeIndex: number, overIndex: number) => void;
  items: Item[];
  DraggableComponent: ComponentType<T & DraggableComponentProps<Item>>;
  idProperty: keyof Item;
  componentProps: T;
  space?: Spacing;
};

export type DraggableComponentProps<Item> = {
  item: Item;
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
};

type DraggingState = {
  id: string;
  order: number;
  index: number;
  componentProps: any;
} | null;

export const SortableList = <T, Item>({
  onChange,
  items,
  DraggableComponent,
  idProperty,
  componentProps,
  space = 2,
}: SortableListProps<T, Item>) => {
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );
  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const order = active.data.current?.order;
    const currentItem = items.find((item) => String(item[idProperty]) === active.id);
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: String(active.id),
      componentProps: {
        ...active.data.current?.componentProps,
        item: currentItem,
      },
    });
  };

  const onDragEnd = async (event: DragEndEvent) => {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;
    const overIndex = itemIds.indexOf(String(over.id));
    const activeIndex = itemIds.indexOf(String(active.id));
    onChange(activeIndex, overIndex);
  };

  const onDragCancel = () => {
    setDraggingItem(null);
  };
  const itemIds = items.map((item) => String(item[idProperty]));

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <DragOverlay>
        {draggingItem && <DraggableComponent {...draggingItem.componentProps} />}
      </DragOverlay>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <Stack space={space}>
          {items.map((item, index) => {
            const itemId = String(item[idProperty]);
            return (
              <DraggableItem<T, typeof item>
                index={index}
                key={itemId}
                itemId={itemId}
                isDragging={draggingItem?.id === itemId}
                Component={DraggableComponent}
                item={item}
                componentProps={componentProps}
              />
            );
          })}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};

type DraggableItemProps<T, Item> = {
  index: number;
  itemId: string;
  isDragging: boolean;
  Component: ComponentType<any>;
  item: Item;
  componentProps: T;
};

const DraggableItem = <T, Item>({
  index,
  itemId,
  isDragging,
  Component,
  item,
  componentProps,
}: DraggableItemProps<T, Item>) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: itemId,
    data: {
      order: index,
      componentProps,
    },
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    ...(isDragging && { opacity: 0.5 }),
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Component item={item} {...componentProps} attributes={attributes} listeners={listeners} />
    </div>
  );
};
